import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { formatDate } from '../../lib/utils';
import { invitationUrlSegment } from '../../lib/slug';

interface ContractRow {
  id: string;
  title: string;
  createdAt: string;
  kolAcceptedAt?: string | null;
  signedAt?: string | null;
  signerName?: string | null;
  signatureType?: string | null;
  template?: { id: string; name: string } | null;
  job?: { id: string; title: string; urlSlug?: string | null; brandOrg?: { name: string } | null } | null;
  invitation?: { id: string; status: string; urlSlug?: string | null } | null;
}

export default function KOLContracts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContracts();
  }, []);

  async function loadContracts() {
    try {
      const res = await api.get('/kols/me/contracts');
      setContracts(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function downloadPdf(contractId: string) {
    api.get(`/contracts/${contractId}/pdf`, { responseType: 'blob' }).then((res) => {
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${contractId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  return (
    <div className="ph-no-capture space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">Contracts</h1>
        <p className="text-glass-text-2 text-sm mt-1">Sign contracts BEFORE accepting invitations.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="page-title text-xl">Your contracts</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-glass-text-2">Loading…</p>
          ) : contracts.length === 0 ? (
            <p className="text-sm text-glass-text-2">
              Nothing here yet. When an agency attaches a contract to your invitation, it will show up.
            </p>
          ) : (
            <ul className="divide-y divide-glass">
              {contracts.map((c) => {
                  const invId = c.invitation?.id;
                  const signed = !!c.signedAt;
                  const legacyAck = !signed && !!c.kolAcceptedAt;
                  const needsSign = !!invId && !signed && !legacyAck;
                  const navUrl =
                    invId && c.invitation
                      ? `/kol/invitations/${encodeURIComponent(
                          invitationUrlSegment({
                            id: c.invitation.id,
                            urlSlug: c.invitation.urlSlug,
                            job: c.job ? { title: c.job.title, urlSlug: c.job.urlSlug } : undefined,
                          })
                        )}`
                      : null;
                  return (
                    <li key={c.id} className="py-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-medium text-glass-text flex items-center gap-2">
                          {c.title}
                          {signed && (
                            <span className="status-pill status-pill-pos inline-flex items-center gap-1">
                              <ShieldCheck size={12} /> Signed
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-glass-text-2">
                          {c.job?.brandOrg?.name || 'Brand'} · {c.job?.title || 'Campaign'}
                        </div>
                        <div className="text-xs text-glass-text-3 mt-1">
                          {c.template?.name || 'Custom'} · Created {formatDate(c.createdAt)}
                          {signed && (
                            <span className="text-[rgb(var(--pos))] ml-1">· Signed {formatDate(c.signedAt!)}</span>
                          )}
                          {legacyAck && (
                            <span className="text-[rgb(var(--pos))] ml-1">
                              · Acknowledged {formatDate(c.kolAcceptedAt!)}
                            </span>
                          )}
                          {needsSign && (
                            <span className="text-[rgb(var(--warn))] ml-1">· Pending signature</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Button type="button" variant="outline" size="sm" onClick={() => downloadPdf(c.id)}>
                          PDF
                        </Button>
                        {needsSign && navUrl && (
                          <Button
                            type="button"
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => navigate(navUrl)}
                          >
                            <ShieldCheck size={14} className="mr-1" />
                            Sign now
                          </Button>
                        )}
                        {!needsSign && navUrl && (
                          <Button type="button" size="sm" onClick={() => navigate(navUrl)}>
                            Open Invitation
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
}